'use client';

/**
 * Full-bleed hero with a looping background video, dark scrim overlay,
 * and an overlaid content column (kicker + title + subtitle + primary
 * & secondary CTAs).
 * Ported from ThemeMaker's {@code blocks/HeroVideo/HeroVideo}.
 *
 * <p>Uses {@code --page-bleed-margin} / {@code --page-bleed-width}
 * (set by the shell) so the section bleeds correctly in both top-header
 * layouts (full viewport width) and sidebar layouts (just the content
 * column).
 */
import { tx, pickEnum, asNumber, asBool } from '../_shared/coerce';
import { HeroVideoBackground } from './HeroVideoBackground';
import { Button } from '@akademi/ui';
import { resolveInsetStyle, type PaddingBox } from '../_shared/resolvePadding';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface HeroVideoProps {
  videoUrl?: string;
  posterImage?: string;
  height?: 'medium' | 'large' | 'full';
  kicker?: Localized;
  title?: Localized;
  subtitle?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  secondaryCtaLabel?: Localized;
  secondaryCtaHref?: string;
  align?: 'left' | 'center' | 'right';
  overlayOpacity?: number;
  overlayGradient?: boolean;
  /** 'full' (default) lets content span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
  radius?: 'none' | 'sm' | 'md' | 'lg';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const RADIUS_MAP: Record<NonNullable<HeroVideoProps['radius']>, string> = {
  none: '0px',
  sm: 'var(--radius-sm, 6px)',
  md: 'var(--radius-md, 12px)',
  lg: 'var(--radius-lg, 16px)',
};

export function HeroVideo({
  videoUrl,
  posterImage,
  height,
  kicker,
  title,
  subtitle,
  ctaLabel,
  ctaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  align,
  overlayOpacity,
  overlayGradient,
  maxWidth = 'full',
  radius = 'none',
  paddingOuter,
  paddingInner,
}: HeroVideoProps) {
  if (!videoUrl) return null;

  const outerInset = resolveInsetStyle(paddingOuter);
  const innerInset = resolveInsetStyle(paddingInner);

  const h = pickEnum<'medium' | 'large' | 'full'>(
    height,
    ['medium', 'large', 'full'] as const,
    'large',
  );
  const a = pickEnum<'left' | 'center' | 'right'>(
    align,
    ['left', 'center', 'right'] as const,
    'left',
  );
  const opacity = asNumber(overlayOpacity, 0.45);
  const gradientOn = asBool(overlayGradient, true);

  const k = tx(kicker);
  const t = tx(title);
  const st = tx(subtitle);
  const cl = tx(ctaLabel);
  const sl = tx(secondaryCtaLabel);

  // A floor, not a fixed height — the section grows past this if the
  // kicker/title/subtitle/CTAs need more room than the vh budget allows
  // (long titles, translations, narrow viewports) instead of clipping
  // them. Unlike HeroVideoBackground, this video has no baked-in visual
  // content to protect via aspect-ratio matching — it's just a backdrop
  // behind separately overlaid text.
  const minHeightStyle =
    h === 'full' ? 'min(100vh, 900px)' : h === 'medium' ? 'min(60vh, 560px)' : 'min(80vh, 720px)';

  const gradient =
    a === 'right'
      ? `linear-gradient(to left, rgba(0,0,0,${opacity}) 0%, rgba(0,0,0,${opacity * 0.4}) 60%, rgba(0,0,0,0) 100%)`
      : a === 'center'
        ? `linear-gradient(to top, rgba(0,0,0,${opacity}) 0%, rgba(0,0,0,${opacity * 0.6}) 50%, rgba(0,0,0,${opacity * 0.3}) 100%)`
        : `linear-gradient(to right, rgba(0,0,0,${opacity}) 0%, rgba(0,0,0,${opacity * 0.4}) 60%, rgba(0,0,0,0) 100%)`;

  const justifyContent = a === 'center' ? 'center' : a === 'right' ? 'flex-end' : 'flex-start';

  return (
    <BlockRoot
      as="section"
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        minHeight: minHeightStyle,
        overflow: 'hidden',
        borderRadius: RADIUS_MAP[radius],
        background: 'var(--color-neutral-900, #0c0a09)',
        color: 'var(--color-text-on-brand, #fff)',
        // 'full' (default) breaks out to the true viewport edge via the
        // page-bleed CSS vars (SiteShellPresenter sets them so blocks
        // adapt to top-bar vs sidebar layouts). 'theme' stays inside
        // the normal content column instead, boxed and centered.
        ...(maxWidth === 'theme'
          ? {
              width: '100%',
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              marginTop: 0,
              marginRight: 'auto',
              marginBottom: 'var(--space-8, 32px)',
              marginLeft: 'auto',
            }
          : {
              marginTop: 0,
              marginLeft: 'var(--page-bleed-margin, calc(50% - 50vw))',
              marginRight: 'var(--page-bleed-margin, calc(50% - 50vw))',
              marginBottom: 'var(--space-8, 32px)',
              width: 'var(--page-bleed-width, 100vw)',
              maxWidth: 'var(--page-bleed-width, 100vw)',
            }),
      }}
    >
      {/* CSS `padding` on this section wouldn't inset an `inset:0` child —
       *  its containing block is the section's padding box, which already
       *  includes the padding area. Explicit inset offsets on these rings
       *  (resolveInsetStyle) are what actually carve out a visible gap. */}
      <div style={{ position: 'absolute', inset: 0, ...outerInset }}>
        <div style={{ position: 'absolute', inset: 0, ...innerInset }}>
          <HeroVideoBackground src={videoUrl} poster={posterImage} />
        </div>
      </div>

      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: gradientOn ? gradient : `rgba(0,0,0,${opacity})`,
          zIndex: 1,
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          display: 'flex',
          justifyContent,
          padding: '0 var(--space-8, 48px)',
          maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
          margin: '0 auto',
          textAlign: a,
        }}
      >
        <div
          style={{
            maxWidth: 640,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3, 12px)',
            alignItems: a === 'center' ? 'center' : 'flex-start',
          }}
        >
          {k && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-widest, 0.12em)',
                textTransform: 'uppercase',
                color: 'var(--color-brand-300, rgba(255,255,255,0.85))',
              }}
            >
              {k}
            </div>
          )}
          {t && (
            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(36px, 5vw, 64px)',
                fontWeight: 800,
                lineHeight: 1.05,
                fontFamily: 'var(--font-family-display, inherit)',
                letterSpacing: 'var(--font-tracking-snug, -0.02em)',
                textShadow: '0 2px 12px rgba(0,0,0,0.4)',
              }}
            >
              {t}
            </h1>
          )}
          {st && (
            <p
              style={{
                margin: 0,
                fontSize: 'clamp(15px, 1.4vw, 19px)',
                lineHeight: 1.5,
                color: 'rgba(255,255,255,0.92)',
                maxWidth: 520,
              }}
            >
              {st}
            </p>
          )}
          {(cl || sl) && (
            <div
              style={{
                marginTop: 'var(--space-3, 12px)',
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                justifyContent: a === 'center' ? 'center' : 'flex-start',
              }}
            >
              {cl && ctaHref && (
                <a
                  href={ctaHref}
                  style={{
                    display: 'inline-block',
                    padding: 'var(--space-3, 12px) var(--space-7, 28px)',
                    borderRadius: 'var(--radius-md, 6px)',
                    background: 'var(--color-brand-500, #f97316)',
                    color: 'var(--color-text-on-brand, #fff)',
                    fontWeight: 600,
                    fontSize: 'var(--font-size-sm, 14px)',
                    textDecoration: 'none',
                  }}
                >
                  {cl}
                </a>
              )}
              {sl && secondaryCtaHref && (
                <Button
                  href={secondaryCtaHref}
                  variant="outline"
                  style={{
                    height: 'auto',
                    padding: 'var(--space-3, 12px) var(--space-7, 28px)',
                    borderRadius: 'var(--radius-md, 6px)',
                    background: 'rgba(255,255,255,0.12)',
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'var(--color-text-on-brand, #fff)',
                    fontWeight: 600,
                    fontSize: 'var(--font-size-sm, 14px)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  {sl}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </BlockRoot>
  );
}
